import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";

const S = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-display font-bold text-navy text-xl sm:text-2xl">{title}</h2>
    <div className="mt-3 space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">{children}</div>
  </div>
);

const Layout = ({ title, intro, children, testid }) => {
  useEffect(() => {
    document.title = `${title} | Your Helper`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div data-testid={testid}>
      <Navbar />
      <main className="pt-[72px]">
        <section className="bg-navy py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <Reveal>
              <Eyebrow dark>Legal</Eyebrow>
              <h1 className="mt-4 font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                {title}
              </h1>
              <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-xl leading-relaxed">{intro}</p>
              <p className="mt-3 font-mono text-xs text-slate-500">Last updated: July 2026</p>
            </Reveal>
          </div>
        </section>
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">{children}</div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export function Terms() {
  return (
    <Layout
      title="Terms & Conditions"
      intro="The rules of the marketplace — what you can expect from us, and what we ask of you."
      testid="terms-page"
    >
      <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
        These terms govern your use of the Your Helper website and mobile app. By creating an account or using
        the platform, you agree to them.
      </p>
      <S title="1. Who we are">
        <p>
          Your Helper is operated by Your Helper Ltd, a company registered in England & Wales. We run a
          marketplace that connects people who need jobs done ("customers") with independent local service
          providers ("taskers"). You can reach us at hello@yourhelper.uk.
        </p>
      </S>
      <S title="2. Who can use the platform">
        <p>
          You must be aged 18 or over to create an account, post jobs or offer services. By using the platform you
          confirm that you meet this requirement.
        </p>
      </S>
      <S title="3. Our role">
        <p>
          Your Helper is a marketplace, not an employer or agency. Taskers are independent service providers,
          and any agreement for work is made directly between the customer and the tasker. We provide the tools to
          find each other, communicate and pay securely.
        </p>
      </S>
      <S title="4. Your account">
        <p>
          Keep your account details accurate and your login secure. You're responsible for activity that happens
          through your account. Tell us straight away at hello@yourhelper.uk if you suspect unauthorised
          access.
        </p>
      </S>
      <S title="5. Posting jobs and accepting offers">
        <p>
          Describe your job honestly and include photos where helpful. Taskers send you offers, and you choose who
          to book based on their profile, ratings, reviews and verification status. A booking is made when you
          accept an offer.
        </p>
      </S>
      <S title="6. Payments and fees">
        <p>
          Posting a job is free. When a job is completed, customers pay a small service fee on top of the agreed
          job price, and taskers pay a service fee on their earnings. Every charge is shown clearly before you
          confirm a booking or accept an offer — there are never hidden costs.
        </p>
        <p>
          All payments are processed securely through our payment provider. Never pay a tasker in cash or outside
          the platform — jobs paid off-platform aren't protected by these terms.
        </p>
      </S>
      <S title="7. Verification and reviews">
        <p>
          Taskers and customers can verify their identity through our verification system. Reviews must reflect
          genuine experiences — we may remove content that is fake, misleading or abusive, and suspend accounts
          that abuse the review system.
        </p>
      </S>
      <S title="8. Cancellations">
        <p>
          You can cancel a booking through the app before work begins. Repeated last-minute cancellations — by
          customers or taskers — may affect your account standing, as they waste the other party's time.
        </p>
      </S>
      <S title="9. Acceptable use">
        <p>
          You must not use the platform for anything unlawful, to arrange work that requires a licensed
          professional where the tasker isn't qualified, to harass other users, or to circumvent platform fees by
          taking payments off-platform.
        </p>
      </S>
      <S title="10. Liability">
        <p>
          We work hard to keep the platform safe and reliable, but the work itself is carried out by independent
          taskers and we can't guarantee their performance. Nothing in these terms excludes liability that cannot
          be excluded under English law. No insurance product is included with bookings unless we expressly say so.
        </p>
      </S>
      <S title="11. Changes to these terms">
        <p>
          We may update these terms as the platform grows. We'll always post the current version here, and if a
          change is significant we'll let you know in the app.
        </p>
      </S>
      <S title="12. Governing law">
        <p>
          These terms are governed by the laws of England & Wales, and the courts of England & Wales have
          exclusive jurisdiction over any dispute relating to them.
        </p>
      </S>
    </Layout>
  );
}

export function Privacy() {
  return (
    <Layout
      title="Privacy Policy"
      intro="What we collect, why we collect it, and the control you have over your data."
      testid="privacy-page"
    >
      <S title="1. Who we are">
        <p>
          Your Helper Ltd is the data controller for the personal information collected through our website
          and app. Contact us about anything in this policy at privacy@yourhelper.uk.
        </p>
      </S>
      <S title="2. Information we collect">
        <p>
          Account details (name, email, phone), profile information, job posts and photos you upload, messages
          sent through the platform, reviews, approximate location, identity verification data, and payment
          information (processed and stored by our payment provider — we never see your full card details). We
          also collect usage data to understand how the app is used.
        </p>
      </S>
      <S title="3. How we use your information">
        <p>
          To run the marketplace: matching customers with taskers, showing offers and profiles, processing
          payments, verifying identities, keeping the platform safe, providing support, and improving the product.
          With your permission, we may also send service updates and marketing you can opt out of at any time.
        </p>
      </S>
      <S title="4. Legal bases">
        <p>
          We process your data to perform our contract with you, for our legitimate interests (safety, fraud
          prevention, product improvement), to comply with legal obligations, and where you have given consent.
        </p>
      </S>
      <S title="5. Who we share it with">
        <p>
          Our payment provider (to process payments), our identity verification provider (to verify users),
          analytics providers (to improve the product), and authorities where the law requires it. We never sell
          your personal data.
        </p>
      </S>
      <S title="6. How long we keep it">
        <p>
          We keep your data for as long as your account is active and for a reasonable period afterwards to meet
          legal, tax and accounting obligations, after which it is deleted or anonymised.
        </p>
      </S>
      <S title="7. Your rights">
        <p>
          Under UK GDPR you can request access to, correction of, or deletion of your data, restrict or object to
          processing, and request data portability. Email privacy@yourhelper.uk to exercise any of these
          rights. You also have the right to complain to the Information Commissioner's Office (ICO).
        </p>
      </S>
      <S title="8. Security">
        <p>
          We use encryption in transit, strict access controls and vetted providers to protect your information.
          No system is perfectly secure, but protecting your data is central to how we build.
        </p>
      </S>
      <S title="9. International transfers">
        <p>
          Some of our providers process data outside the UK. Where this happens, we make sure appropriate
          safeguards (such as UK-approved contractual clauses) are in place.
        </p>
      </S>
      <S title="10. Changes and contact">
        <p>
          We'll post any changes to this policy here and update the date above. Questions? Email
          privacy@yourhelper.uk.
        </p>
      </S>
    </Layout>
  );
}

export function Cookies() {
  return (
    <Layout
      title="Cookie Policy"
      intro="The small files that help the site work and help us understand how it's used."
      testid="cookies-page"
    >
      <S title="1. What are cookies?">
        <p>
          Cookies are small text files stored on your device when you visit a website. They help the site function
          and help us understand how people use it.
        </p>
      </S>
      <S title="2. Cookies we use">
        <p>
          <strong className="text-navy">Essential cookies</strong> — required for the site to work, such as
          keeping you logged in. The site won't function without these.
        </p>
        <p>
          <strong className="text-navy">Analytics cookies</strong> — we use product analytics to understand which
          pages are useful and where people get stuck, so we can improve the experience. These don't identify you
          personally.
        </p>
        <p>
          <strong className="text-navy">Preference cookies</strong> — remember choices you make, like your
          location or display preferences.
        </p>
      </S>
      <S title="3. Managing cookies">
        <p>
          You can control or delete cookies through your browser settings at any time. Blocking essential cookies
          may stop parts of the site from working properly.
        </p>
      </S>
      <S title="4. Changes and contact">
        <p>
          We'll update this page if the cookies we use change. Questions? Email privacy@yourhelper.uk.
        </p>
      </S>
    </Layout>
  );
}

export function Complaints() {
  return (
    <Layout
      title="Complaints"
      intro="Something gone wrong? Tell us — we take every complaint seriously."
      testid="complaints-page"
    >
      <S title="1. How to make a complaint">
        <p>
          Email hello@yourhelper.uk with the subject line "Complaint", or contact us through the Help
          section in the app.
        </p>
      </S>
      <S title="2. What to include">
        <p>
          Your full name, the email address on your account, the job or booking reference if you have one, and a
          clear description of what happened and what you'd like us to do. Screenshots or photos help us
          investigate faster.
        </p>
      </S>
      <S title="3. What happens next">
        <p>
          We'll acknowledge your complaint within 2 working days and aim to give you a full response within 10
          working days. If the issue is complex and takes longer, we'll keep you updated on progress.
        </p>
      </S>
      <S title="4. If you're not satisfied">
        <p>
          Reply to our response and ask for a senior review — a member of the leadership team will look at your
          case again and send you a final response. If you're still unhappy after our final response, you may wish
          to seek independent advice about alternative dispute resolution or the small claims court.
        </p>
      </S>
      <S title="5. Payment disputes">
        <p>
          If your complaint concerns a payment, you can also raise a dispute with your bank or card provider.
          Please contact us first wherever possible — most issues are resolved fastest when we can look into them
          directly.
        </p>
      </S>
    </Layout>
  );
}
